"use client";

import { useState } from "react";
import { Plus, ChevronRight, Settings2 } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
export default function Hardware() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const components = [{
    id: "hinge",
    label: "Hinge",
    number: "01",
    name: "Heavy-Duty Pivot",
    description: "Industrial-grade stainless steel pivot hinge designed for high-traffic commercial doors. Engineered for silent operation and long-term structural integrity.",
    spec1_label: "Material",
    spec1_value: "316L Steel",
    spec2_label: "Load",
    spec2_value: "150 kg",
    spec3_label: "Finish",
    spec3_value: "Brushed",
    top: "32%",
    left: "82%"
  }, {
    id: "handle",
    label: "Handle",
    number: "02",
    name: "Architectural Lever",
    description: "Precision-machined lever handle with a minimalist profile. Ergonomically balanced for corporate environments, featuring a fingerprint-resistant matte finish.",
    spec1_label: "Hardness",
    spec1_value: "High",
    spec2_label: "Thickness",
    spec2_value: "12 mm",
    spec3_label: "Coating",
    spec3_value: "Anodized",
    top: "42%",
    left: "48%"
  }, {
    id: "lock",
    label: "Locking",
    number: "03",
    name: "Security Mortise",
    description: "High-security mortise lockset providing robust protection for office suites. Compatible with standard master key systems for corporate facility management.",
    spec1_label: "Material",
    spec1_value: "Steel Alloy",
    spec2_label: "Count",
    spec2_value: "3 Bolts",
    spec3_label: "Setting",
    spec3_value: "Internal",
    top: "55%",
    left: "38%"
  }, {
    id: "closer",
    label: "Closer",
    number: "04",
    name: "Hydraulic Closer",
    description: "Adjustable hydraulic door closer ensuring smooth, controlled closing cycles. Designed to meet international fire safety and accessibility standards.",
    spec1_label: "Construction",
    spec1_value: "Hydraulic",
    spec2_label: "Width",
    spec2_value: "Adjustable",
    spec3_label: "Finish",
    spec3_value: "Silver",
    top: "68%",
    left: "62%"
  }, {
    id: "stop",
    label: "Stop",
    number: "05",
    name: "Floor Stopper",
    description: "Low-profile floor-mounted door stop with rubber dampening. Prevents wall damage while maintaining a clean, unobtrusive aesthetic in modern interiors.",
    spec1_label: "Origin",
    spec1_value: "Imported",
    spec2_label: "Thickness",
    spec2_value: "25 mm",
    spec3_label: "Stitching",
    spec3_value: "N/A",
    top: "84%",
    left: "28%"
  }];
  const [activeId, setActiveId] = useState<string>("handle");
  const active = components.find(c => c.id === activeId) ?? components[1];
  return <section data-section-id="5188" id="architectural-hardware" className="bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      <div ref={ref} className={`mx-auto max-w-[1400px] px-6 transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {}
          <div className="relative lg:col-span-7 order-2 lg:order-1">
            <div className="relative w-full aspect-[4/5] lg:aspect-square overflow-hidden rounded-lg border border-accent-metallic/30 bg-card">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/architectural_hardware_macro.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/architectural_hardware_macro.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/architectural_hardware_macro.png-wvc-sizes" alt="Macro shot of premium architectural hardware components" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-60 grayscale hover:grayscale-0 transition-all duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />

              {}
              <div className="absolute inset-0">
                {components.map((c, i) => <Button key={c.id} data-index={i} variant="ghost" onMouseEnter={() => setActiveId(c.id)} onClick={() => setActiveId(c.id)} aria-label={`View details for ${c.label}`} className="absolute -translate-x-1/2 -translate-y-1/2 group z-10 p-0 h-auto hover:bg-transparent" style={{
                top: c.top,
                left: c.left
              }}>
                    <span className={`absolute inset-0 -m-4 rounded-lg transition-all duration-500 ${activeId === c.id ? "bg-primary/20 scale-100" : "bg-primary/5 scale-0 group-hover:scale-75"}`} />
                    <div className={`relative flex items-center justify-center size-6 rounded-lg border transition-all duration-300 ${activeId === c.id ? "bg-primary border-primary shadow-[0_0_15px_rgba(14,165,233,0.5)]" : "bg-background/80 border-accent-metallic group-hover:border-primary"}`}>
                      <Plus className={`size-3 transition-transform duration-300 ${activeId === c.id ? "rotate-45 text-white" : "text-primary"}`} />
                    </div>
                  </Button>)}
              </div>

              {}
              <div className="absolute bottom-8 left-8 right-8 flex justify-between items-end pointer-events-none">
                <div className="space-y-1">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-primary">
                    System Status: Active
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-accent-metallic/50">
                    Ref: WS-HW-2024-001
                  </div>
                </div>
                <div className="size-16 border-r border-b border-accent-metallic/30 rounded-lg" />
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-5 order-1 lg:order-2">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-6 flex items-center gap-3">
              <Settings2 className="size-4" />
              <span>Engineering / 03</span>
            </div>

            <h2 className="font-sans text-5xl sm:text-6xl lg:text-7xl font-bold leading-[0.9] text-foreground mb-8 tracking-tighter">
              PRECISION <br />
              <span className="text-accent-metallic/40">HARDWARE</span>
            </h2>

            <p className="font-sans text-base text-muted-foreground leading-relaxed max-w-md mb-12">
              World Sky provides the essential components for modern construction. Our architectural hardware is engineered for extreme durability and seamless integration.
            </p>

            {}
            <div key={active.id} className="relative bg-card border border-accent-metallic/20 p-8 lg:p-10 rounded-lg shadow-xl animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="flex justify-between items-start mb-8">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-primary mb-2">
                    Component {active.number}
                  </div>
                  <h3 className="font-sans text-3xl font-bold text-foreground tracking-tight">
                    {active.name}
                  </h3>
                </div>
                <div className="font-mono text-2xl font-light text-accent-metallic/20">
                  {active.number}
                </div>
              </div>

              <p className="font-sans text-sm text-muted-foreground leading-relaxed mb-10">
                {active.description}
              </p>

              {}
              <div className="grid grid-cols-1 gap-4 mb-10">
                {[{
                label: active.spec1_label,
                value: active.spec1_value
              }, {
                label: active.spec2_label,
                value: active.spec2_value
              }, {
                label: active.spec3_label,
                value: active.spec3_value
              }].map((s, i) => <div key={i} data-index={i} className="flex items-center justify-between border-b border-accent-metallic/10 pb-3">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-accent-metallic/60">
                      {s.label}
                    </span>
                    <span className="font-sans text-sm font-medium text-foreground">
                      {s.value}
                    </span>
                  </div>)}
              </div>

              <Button className="w-full bg-primary hover:bg-primary/90 text-white rounded-lg py-6 flex items-center justify-center gap-2 group">
                Request Technical Data
                <ChevronRight className="size-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </div>

        {}
        <div className="hidden lg:grid grid-cols-5 mt-24 border-t border-accent-metallic/20 pt-10">
          {components.map((c, i) => <Button key={c.id} data-index={i} variant="ghost" onMouseEnter={() => setActiveId(c.id)} onClick={() => setActiveId(c.id)} className={`group text-left transition-all duration-300 px-4 border-l border-transparent hover:border-primary/30 h-auto p-0 flex flex-col items-start hover:bg-transparent ${activeId === c.id ? "opacity-100" : "opacity-40 hover:opacity-70"}`}>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] mb-3 text-primary">
                {c.number}
              </div>
              <div className="font-sans text-xs font-bold uppercase tracking-widest text-foreground">
                {c.label}
              </div>
              <div className={`mt-4 h-0.5 transition-all duration-500 rounded-lg ${activeId === c.id ? "bg-primary w-full" : "bg-accent-metallic/20 w-4 group-hover:w-8"}`} />
            </Button>)}
        </div>

        {}
        <div className="lg:hidden mt-12 -mx-6 px-6 overflow-x-auto no-scrollbar">
          <div className="flex gap-4 pb-4 w-max">
            {components.map((c, i) => <Button key={c.id} data-index={i} variant="ghost" onClick={() => setActiveId(c.id)} className={`shrink-0 border p-4 text-left transition-all duration-300 rounded-lg min-w-[140px] h-auto flex flex-col items-start hover:bg-transparent ${activeId === c.id ? "border-primary bg-primary/5" : "border-accent-metallic/20 bg-card"}`}>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] mb-1 text-primary">
                  {c.number}
                </div>
                <div className="font-sans text-xs font-bold uppercase tracking-widest text-foreground">
                  {c.label}
                </div>
              </Button>)}
          </div>
        </div>
      </div>
    </section>;
}